<template>
  <div>
    <Overlay
      class="cover__load"
      v-model:active="props.loading"
      :is-full-page="false"
      color="#b20000"
      background-color="#000"
      :opacity="0"
    />
    <section
      class="cover"
      :class="{ 'cover--teaser-vertical': temTeaser }"
      v-if="props.cardPrincipal"
    >
      <NuxtLink
        :to="{
          name: 'language-modelos-name-photo',
          params: { name: props.cardPrincipal.url },
        }"
      >
        <div :class="`cover__image ${opac}`">
          <!-- O teaser e o carrossel dependem da busca do cliente: a URL do
               teaser é assinada e não vem no destaque do SSR. O pôster e o
               width/height seguram a caixa da imagem até o vídeo carregar -->
          <video
            v-if="montado && cardPrincipal.videoPreview"
            playsInline
            autoPlay
            muted
            loop
            class="video-home"
            :poster="posterTeaser"
            :width="teaserMobile ? 412 : 1440"
            :height="teaserMobile ? 302 : 715"
          >
            <source
              :src="encodeURI(cardPrincipal.videoPreview)"
              type="video/mp4"
            />
          </video>
          <CarouselEssayMain
            v-else-if="montado && cardPrincipal.videoPreview === ''"
            :card-images="
              !isMobile()
                ? props.cardPrincipal.home
                : props.cardPrincipal.homeMobile
            "
            :model-name="props.cardPrincipal.name"
            :image-alt="bannerAlt"
          />
          <!-- Até lá, a 1ª imagem do destaque, já no HTML do SSR: é o
               elemento LCP da home, com preload no <head> da página -->
          <picture v-else>
            <source
              v-if="imagemMobile"
              media="(max-width: 769px)"
              :srcset="imagemMobile"
              width="412"
              height="302"
            />
            <img
              id="cover-image"
              :src="imagemDesktop"
              width="1440"
              height="715"
              fetchpriority="high"
              :alt="bannerAlt"
            />
          </picture>
        </div>
        <div class="cover__info">
          <div class="container cont-top">
            <h2 class="cover__title">{{ props.cardPrincipal.name }}</h2>
            <h3 class="cover__description">
              {{ props.cardPrincipal.description }}
            </h3>
            <p class="cover__text" v-if="props.cardPrincipal.text">
              {{ props.cardPrincipal.text }}
            </p>
            <!-- span, não link: a capa inteira já é o link para o mesmo
                 ensaio, e <a> dentro de <a> no HTML do SSR faz o navegador
                 reorganizar a árvore (quebra a hidratação) -->
            <span class="button button--o">
              <span class="txt-button">{{
                translate.buttomImageCardPrincipal
              }}</span>
              <font-awesome
                :icon="['fas', 'arrow-right']"
                size="lg"
                class="btn-arrow"
              />
            </span>
          </div>
        </div>
      </NuxtLink>
    </section>
    <section v-else class="cover">
      <!-- desktop e mobile por CSS: o SSR não sabe a largura da tela -->
      <picture>
        <source media="(max-width: 769px)" :srcset="imgsMobile" />
        <img :src="imgsDesktop" :alt="bannerAlt" />
      </picture>
    </section>
    <section class="block block--black">
      <div class="container">
        <div class="model-list model-list--home">
          <swiper
            v-if="props.ultimosCards?.length"
            :slidesPerView="1.07"
            :spaceBetween="10"
            :pagination="{ dynamicBullets: true, clickable: true }"
            :breakpoints="{
              '770': {
                slidesPerView: 2,
                spaceBetween: 10,
              },
              '1040': {
                slidesPerView: 2,
                spaceBetween: 10,
              },
            }"
            :modules="[Pagination]"
            class="mySwiper swp-sizes"
            :style="`--swiper-pagination-color:white;--swiper-pagination-bullet-inactive-color:white;--swiper-pagination-bullet-size:12px;`"
          >
            <swiper-slide
              v-for="(item, index) in props.ultimosCards"
              :key="index"
            >
              <model-list-item
                :name="item.name"
                :slug="item.url"
                :image="item.imgUrl"
                :index="index"
                :mobile-image="item.imgUrl"
                :image-alt-text="item.imageAltText"
              ></model-list-item>
            </swiper-slide>
          </swiper>
          <!-- Até a busca do cliente chegar, a caixa de um card: no mobile a
               altura não é fixa, e os cards empurravam o resto da página -->
          <div v-else class="swp-sizes swp-reserva" aria-hidden="true">
            <div class="swp-reserva__card">
              <div class="swp-reserva__imagem"></div>
              <div class="swp-reserva__info"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import CarouselEssayMain from "@/components/HomePage/CarouselEssayMain.vue";
import Overlay from "vue-loading-overlay";
import ModelListItem from "@/components/HomePage/ModelListItem.vue";
import translateMain from "@/tradutors/menu";
import { isMobile } from "@/shared/utils/mobile";

// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from "swiper/vue";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

// import required modules
import { Pagination } from "swiper/modules";

//Preload
import ImgPreloadDesktop from "@/assets/img/home-desktop.webp";
import ImgPreloadMobile from "@/assets/img/home-mobile.webp";

const props = defineProps(["cardPrincipal", "ultimosCards", "loading"]);

const imgsDesktop = ImgPreloadDesktop;
const imgsMobile = ImgPreloadMobile;
// Com o destaque vindo do SSR, a imagem já sai visível no HTML.
const opac = ref(props.cardPrincipal ? "opacity-1" : "opacity-0");
const montado = ref(false);
onMounted(() => {
  montado.value = true;
});

watch(
  () => props.cardPrincipal,
  () => {
    opac.value = "opacity-1";
  }
);

const imagemDesktop = computed(
  () => props.cardPrincipal?.home?.[0] ?? props.cardPrincipal?.homeMobile?.[0]
);
const imagemMobile = computed(() => props.cardPrincipal?.homeMobile?.[0]);
// Pôster do teaser: a mesma imagem do banner, no formato do teaser que a busca
// do cliente pediu (isMobile(), como em getCardPrincipal). Sem ele o vídeo
// entra sem tamanho até carregar, e o banner encolhe e cresce de novo; se o
// teaser falhar, a imagem continua no lugar, em vez da caixa preta.
const teaserMobile = computed(() => montado.value && isMobile());
// Há teaser: o destaque do SSR traz só o booleano (temTeaser); depois da busca
// do cliente, a própria URL. No mobile o banner já reserva a caixa vertical dele.
const temTeaser = computed(
  () =>
    !!props.cardPrincipal?.videoPreview || props.cardPrincipal?.temTeaser === true
);
const posterTeaser = computed(() =>
  teaserMobile.value
    ? imagemMobile.value ?? imagemDesktop.value
    : imagemDesktop.value
);
const route = useRoute();
const translate = computed(() =>
  route.params.language == "pt-br"
    ? translateMain.portugues
    : translateMain.ingles
);

const bannerAlt = computed(() => {
  const card = props.cardPrincipal;
  const isPt = route.params.language == "pt-br";
  if (card && card.imageAltText) return card.imageAltText;
  if (card && card.name)
    return isPt
      ? `${card.name} em ensaio sensual exclusivo no Bella da Semana`
      : `${card.name} in an exclusive sensual photo shoot on Bella Club`;
  return isPt
    ? "Ensaio sensual exclusivo no Bella da Semana"
    : "Exclusive sensual photo shoot on Bella Club";
});
</script>

<style scoped lang="css">
.cover__info {
  width: 1440px;
  height: 715px;
}

.cover__load {
  width: 100%;
  height: 215px;
  position: absolute;
  top: 77px;
  z-index: 99;
  left: 50%;
}

/* A imagem do SSR tem a mesma caixa do teaser, que entra no lugar dela depois
   de montar: fixa a partir de 1200 px, fluida abaixo (altura pela proporção de
   width/height; o <source> do mobile tem a dele). Com a imagem fluida entre
   1200 e 1439 px, o banner crescia ~85 px quando o vídeo entrava. */
.video-home,
.cover__image img {
  width: 1440px;
  height: 715px;
}

.cover__image img {
  object-fit: cover;
}

/* Desktop: a reserva dos cards é a própria caixa fixa do .swp-sizes */
.swp-reserva__card {
  display: none;
}

.swiper-slide {
  width: 496px !important;
  margin-right: 25px !important;
}

.model-list--home {
  margin-top: 0px;
  width: 1017px;
  height: 450px;
}

.model-list--home >>> .model-list__item {
  width: 100%;
  height: 100%;
}

.model-list--home >>> .model-list__title {
  font-family: "Roboto-Medium";
  height: 28px;
  color: #ffffff;
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
}

.model-list--home >>> .link {
  height: 19px;
  color: #999;
  font-family: "Exo-2";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
  text-align: right;
}

h3 h2 p {
  font-family: "Roboto-Medium";
}

.cover__description {
  color: #ffffff;
  font-family: "Roboto-Medium";
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
  margin-top: 15px;
}

.cover__title {
  color: #ffffff;
  font-family: "Bebas-Neue";
  font-size: 66px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 56px;
}

.cover__text {
  height: 76px;
  color: #ffffff;
  font-family: "Roboto-Regular";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
  margin-bottom: 52px;
}

.btn-arrow {
  color: #ffffff;
  margin-left: 10px;
 }

:deep(.model-list__image img) {
  cursor: pointer;
  width: 496px;
  height: 364px;
}

.button {
  padding: 0 28px;
  background-color: #b20000;
  border: 0;
}

.cont-top {
  max-width: 1248px;
}

.block--black {
  width: 100%;
  height: 100%;
  padding-bottom: unset;
}

.swp-sizes {
  width: 1017px;
  height: 450px;
  top: -35px;
}

@media screen and (max-width: 1140px) {
  .cover__text {
    display: none;
  }
}

@media screen and (max-width: 1040px) {
  .swiper-slide {
    width: 50% !important;
    left: 25%;
  }

  .cover__info {
    height: 450px;
  }
  .video-home,
  .cover__image img {
    width: 100%;
    height: auto;
  }
}

@media screen and (max-width: 769px) {
  .cont-top {
    text-align: center;
  }

  .cover__title {
    display: block;
    font-size: 48px;
    line-height: 48px;
    margin-top: 20px;
  }

  .video-home {
    width: 100%;
    height: auto;
  }

  .cover__info {
    position: relative;
    margin-bottom: 40px;
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    width: 85% !important;
    height: auto !important;
    left: 0%;
    margin-right: 12px !important;
  }

  /* altura proporcional à largura (imagem 496x364): height auto evita o
     "espichamento" causado pela altura fixa anterior */
  :deep(.model-list__image img) {
    width: 100%;
    height: auto;
  }

  .model-list--home {
    margin-top: 0px;
    width: 100%;
    height: auto;
    padding-left: 0px;
  }

  .cover__description {
    margin-top: unset;
    max-width: 100%;
  }

  :deep(.model-list__info) {
    padding-right: 0px;
    padding-bottom: 15px;
  }

  .model-list--home >>> .model-list__title {
    font-family: "Roboto-Medium";
    height: 28px;
    color: #ffffff;
    font-size: 20px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 28px;
  }

  .model-list--home >>> .link {
    height: 19px;
    color: #999999;
    font-family: "Exo-2";
    font-size: 14px;
    letter-spacing: 0;
    line-height: 19px;
    text-align: right;
    top: -3px;
  }

  .swiper-slide-next >>> .model-list__title {
    padding-left: 30px;
  }

  .swp-sizes {
    width: 103%;
    height: auto;
    top: 0px;
  }

  /* Mesma caixa de um card do swiper (slide de 85%, imagem 496x364 e o bloco
     de título e link), enquanto os cards não chegam */
  .swp-reserva__card {
    display: block;
    width: 85%;
  }
  .swp-reserva__imagem {
    aspect-ratio: 496 / 364;
  }
  .swp-reserva__info {
    height: 62px;
  }

  /* Destaque com teaser: a caixa vertical do teaser (390x474) desde o HTML do
     SSR, com a imagem recortada nela; o vídeo entra na mesma caixa, também
     recortado, então o banner não muda de tamanho quando ele carrega (antes
     crescia ~190 px), seja qual for a proporção do arquivo enviado */
  .cover--teaser-vertical .video-home,
  .cover--teaser-vertical .cover__image img {
    width: 100%;
    height: auto;
    aspect-ratio: 390 / 474;
    object-fit: cover;
  }
}

/* regras mobile 400px */
@media screen and (max-width: 400px) {
  .cover__title {
    display: block;
    font-size: 34px;
    line-height: 36px;
    margin-top: 20px;
    margin-bottom: unset;
  }

  .cover__description {
    font-size: 20px;
    margin-top: unset;
    max-width: 100%;
    font-family: "Roboto-Regular";
  }
}

/* === Tablet/iPad (770–1199px): escala o layout desktop para caber === */
@media screen and (min-width: 770px) and (max-width: 1199px) {
  /* contêineres com largura fixa -> fluidos (evita overflow/corte) */
  .cover__info {
    width: 100%;
  }
  .video-home,
  .cover__image img {
    width: 100%;
    height: auto;
  }
  .model-list--home,
  .swp-sizes {
    width: 100%;
  }
  .cont-top {
    max-width: 100%;
    padding-left: 24px;
    padding-right: 24px;
    top: -30px;
  }

  /* tipografia do hero proporcional ao tablet */
  .cover__title {
    font-size: 52px;
    line-height: 1.05;
  }
  .cover__description {
    font-size: 20px;
    line-height: 24px;
    max-width: 40%;
    margin: 0 0 10px 0;
  }
  /* mantém o texto do hero (como no desktop; some apenas <770 e 1041–1140) */
  .cover__text {
    display: block;
    max-width: 40%;
    font-size: 12px;
  }

  /* "últimos cards": 2 por vez, largura fluida (em vez de 1 card centralizado) */
  .swiper-slide {
    width: 49% !important;
    left: 0 !important;
    margin-right: 18px !important;
  }
  :deep(.model-list__image img) {
    cursor: pointer;
    width: 100%;
    height: auto;
  }

}

@media screen and (min-width: 770px) and (max-width: 850px) {
  .model-list--home {
    height: 360px;
  }
}

@media screen and (min-width: 851px) and (max-width: 1199px) {
  .model-list--home {
    height: 420px;
  }
}
</style>
